import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminApi, type AdminPayoutAlertsResponse } from '../services/api';

type PayoutAlert = AdminPayoutAlertsResponse['items'][number];

export function AdminPayoutAlerts({ projectId }: { projectId?: string }) {
  const { t, i18n } = useTranslation();
  const [alerts, setAlerts] = useState<AdminPayoutAlertsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ key: string; danger: boolean } | null>(null);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setAlerts(await adminApi.getPayoutAlerts(projectId));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { void refresh(); }, [refresh]);
  const retry = async (alert: PayoutAlert) => {
    if (!alert.payoutId || retrying) return;
    setRetrying(alert.payoutId);
    setFeedback(null);
    try {
      const result = await adminApi.retryPayout(alert.milestoneId, alert.payoutId);
      const waiting = result.status === 'awaiting_funds';
      const started = ['pending', 'booked'].includes(result.status);
      setFeedback({ key: waiting ? 'retryWaiting' : started ? 'retryStarted' : 'retryFailed', danger: !waiting && !started });
      await refresh();
    } catch {
      setFeedback({ key: 'retryFailed', danger: true });
    } finally {
      setRetrying(null);
    }
  };

  const isWaiting = (alert: PayoutAlert) => alert.payoutAvailability?.status === 'awaiting_funds';
  const items = (alerts?.items ?? []).filter(alert => projectId
    ? alert.projectId === projectId
    : !isWaiting(alert) || alert.payoutAvailability?.overdue);
  if (!failed && items.length === 0) return feedback
    ? <p className={`alert alert-${feedback.danger ? 'danger' : 'info'}`} role={feedback.danger ? 'alert' : 'status'}>{t(`adminPayoutAlerts.${feedback.key}`)}</p>
    : null;
  const locale = i18n.language.startsWith('en') ? 'en-GB' : 'sv-SE';
  const attentionCount = items.filter(alert => !isWaiting(alert) || alert.payoutAvailability?.overdue).length;
  const waitingCount = items.filter(alert => isWaiting(alert) && !alert.payoutAvailability?.overdue).length;
  const attention = failed || attentionCount > 0;
  const tone = attention ? 'danger' : 'info';
  return (
    <section className={`admin-payout-alerts alert alert-${tone} border-${tone} border-3 mb-4 text-break`} role={attention ? 'alert' : 'status'} data-testid="admin-payout-alerts" aria-labelledby="admin-payout-alerts-title">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <h2 className="h4 fw-bold mb-0" id="admin-payout-alerts-title">
          <i className={`bi bi-${attention ? 'exclamation-octagon-fill' : 'clock'} me-2`} aria-hidden="true" />
          {t(`adminPayoutAlerts.${attention ? 'title' : 'waitingTitle'}`)}
        </h2>
        <button type="button" className={`btn btn-outline-${tone} px-3 py-2`} disabled={loading || !!retrying} onClick={() => void refresh()}>
          {t('adminPayoutAlerts.refresh')}
        </button>
      </div>
      {failed && <p className="fw-bold mt-3 mb-0">{t('adminPayoutAlerts.loadError')}</p>}
      {feedback && <p className={`alert alert-${feedback.danger ? 'danger' : 'info'} mt-3 mb-0`} role={feedback.danger ? 'alert' : 'status'}>{t(`adminPayoutAlerts.${feedback.key}`)}</p>}
      {!!attentionCount && <p className="mt-3">{t('adminPayoutAlerts.summary', { count: attentionCount })}</p>}
      {!!waitingCount && <p className="mt-3">{t('adminPayoutAlerts.waitingSummary', { count: waitingCount })}</p>}
      {items.length > 0 && <>
        <ul className="list-unstyled mb-0">
          {items.map(alert => {
            const availability = alert.payoutAvailability;
            const waiting = isWaiting(alert);
            const rowTone = waiting && !availability?.overdue ? 'info' : 'danger';
            return (
            <li key={alert.milestoneId} className={`border-top border-${rowTone} pt-3 mt-3${attention && rowTone === 'info' ? ' alert alert-info' : ''}`}>
              <h3 className="h5 fw-bold">{t('adminPayoutAlerts.context', { project: alert.projectNumber, contract: alert.contractNumber, milestone: alert.milestoneNumber })}</h3>
              <p className="mb-1 fw-bold">{alert.description}</p>
              <p className="mb-1">{t('adminPayoutAlerts.contractor', { name: alert.companyName })}</p>
              <p className="mb-1">{t('adminPayoutAlerts.amount', { amount: new Intl.NumberFormat(locale, { style: 'currency', currency: 'SEK' }).format(alert.amountOre / 100) })}</p>
              <p className="mb-2">{t(`adminPayoutAlerts.${waiting ? 'waitingRecordedAt' : 'blockedAt'}`, { date: new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Stockholm' }).format(new Date(alert.blockedAt)) })}</p>
              {waiting ? <>
                <p className="fw-bold">{t(`adminPayoutAlerts.${availability?.overdue ? 'overdueBody' : 'waitingBody'}`)}</p>
                {availability?.expectedAvailableAt && <p>{t('adminPayoutAlerts.expectedDate', { date: new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Stockholm' }).format(new Date(availability.expectedAvailableAt)) })}</p>}
              </> : <p className="fw-bold">{t(`adminPayoutAlerts.reasons.${alert.reason}`)}</p>}
              <div className="d-flex flex-wrap gap-2">
                {projectId && waiting && alert.payoutId && <button type="button" className={`btn btn-${rowTone} px-3 py-2`} disabled={!!retrying || loading} onClick={() => void retry(alert)}>{t('adminPayoutAlerts.retry')}</button>}
                <Link className={`btn btn-outline-${rowTone} px-3 py-2`} to={`/admin/company-list/${alert.companyId}`}>{t('adminPayoutAlerts.reviewContractor')}</Link>
                <Link className={`btn btn-outline-${rowTone} px-3 py-2`} to={`/admin/projects/${alert.projectId}?tab=transactions`}>{t('adminPayoutAlerts.reviewProject')}</Link>
              </div>
            </li>
            );
          })}
        </ul>
        {alerts && alerts.total > alerts.items.length && <p className="mt-3 mb-0">{t('adminPayoutAlerts.limited', { count: alerts.items.length, total: alerts.total })}</p>}
      </>}
    </section>
  );
}
